import {
  QuestionButton,
  Tray,
  UnderlyingYieldBearingAssetListItem,
  YieldBearingAssetAboutSection,
  YieldBearingAssetCompositionSection,
  YieldBearingAssetListItem,
} from '@/components';
import { TrayScreen } from '@/components/atoms/Tray/interfaces/Tray';
import { InfoAlert, LoadingAlert } from '@/components/molecules';
import MarkdownRenderer from '@/components/molecules/MarkdownRenderer/MarkdownRenderer';
import {
  TrayNavigationProvider,
  useTrayNavigation,
} from '@/components/providers/common/TrayNavigationProvider';
import APP_CONFIG from '@/config/app';
import {
  UnderlyingYieldBearingAssetInterface,
  YieldBearingAssetInterface,
} from '@/interfaces';
import { MESSAGES } from '@/utils/strings';
import { FC, ReactNode, useEffect, useMemo, useState } from 'react';
const { MAIN_GAP } = APP_CONFIG.STYLES;
const spacingConfig = {
  gap: `gap${MAIN_GAP}`,
};
interface YieldBearingAssetAdvancedDetailsTrayProps {
  isOpen: boolean;
  onClose: () => void;
  asset: YieldBearingAssetInterface;
}
const YieldBearingAssetAdvancedDetailsTrayContent: FC<
  YieldBearingAssetAdvancedDetailsTrayProps
> = ({ isOpen, onClose, asset }) => {
  const [isBottomDrawerOpen, setIsBottomDrawerOpen] = useState(false);
  const [bottomDrawerContent, setBottomDrawerContent] = useState<{
    headerTitle: ReactNode;
    headerContent: ReactNode;
    bodyChildren: ReactNode;
  }>({
    headerTitle: '',
    headerContent: <></>,
    bodyChildren: <></>,
  });
  const {} = useTrayNavigation();
  useEffect(() => {
    if (!isOpen) {
      setIsBottomDrawerOpen(false);
    }
  }, [isOpen]);
  const handleClickOnHelpButton = () => {
    setBottomDrawerContent({
      headerTitle: 'Composition',
      headerContent: <></>,
      bodyChildren: (
        <InfoAlert
          title={
            MESSAGES.YIELD_BEARING_ASSET_DETAIL.COMPOSITION_EXPLANATION_TITLE
          }
          description={
            MESSAGES.YIELD_BEARING_ASSET_DETAIL
              .COMPOSITION_EXPLANATION_DESCRIPTION
          }
        />
      ),
    });
    setIsBottomDrawerOpen(true);
  };
  const handleClickOnBrochureButton = (
    markdown: string,
    asset: UnderlyingYieldBearingAssetInterface,
  ) => {
    setBottomDrawerContent({
      headerTitle: '',
      headerContent: (
        <UnderlyingYieldBearingAssetListItem
          underlyingYieldBearingAsset={asset}
        />
      ),
      bodyChildren: (
        <LoadingAlert
          title={MESSAGES.YIELD_BEARING_ASSET_DETAIL.RESEARCH.LOADING_TITLE}
          description={
            MESSAGES.YIELD_BEARING_ASSET_DETAIL.RESEARCH.LOADING_DESCRIPTION
          }
        />
      ),
    });
    setIsBottomDrawerOpen(true);
    const randomDelay =
      Math.random() *
        (APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MAX_MS -
          APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MIN_MS) +
      APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MIN_MS;
    setTimeout(() => {
      setBottomDrawerContent((prevContent) => ({
        ...prevContent,
        bodyChildren: <MarkdownRenderer markdown={markdown} />,
      }));
    }, randomDelay);
  };
  const handleClickOnYieldBearingAssetBrochureButton = (
    markdown: string,
    asset: YieldBearingAssetInterface,
  ) => {
    setBottomDrawerContent({
      headerTitle: '',
      headerContent: (
        <YieldBearingAssetListItem yieldBearingAsset={asset} isHeader />
      ),
      bodyChildren: (
        <LoadingAlert
          title={MESSAGES.YIELD_BEARING_ASSET_DETAIL.RESEARCH.LOADING_TITLE}
          description={
            MESSAGES.YIELD_BEARING_ASSET_DETAIL.RESEARCH.LOADING_DESCRIPTION
          }
        />
      ),
    });
    setIsBottomDrawerOpen(true);
    const randomDelay =
      Math.random() *
        (APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MAX_MS -
          APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MIN_MS) +
      APP_CONFIG.TIMING.RESEARCH_DRAWER_LOADING_DELAY.MIN_MS;
    setTimeout(() => {
      setBottomDrawerContent((prevContent) => ({
        ...prevContent,
        bodyChildren: <MarkdownRenderer markdown={markdown} />,
      }));
    }, randomDelay);
  };
  const isSingleAsset = asset.underlyingYieldBearingAssets.length === 1;
  const screens: TrayScreen[] = useMemo(
    () => [
      {
        headerNode: (
          <YieldBearingAssetListItem yieldBearingAsset={asset} isHeader />
        ),
        content: (
          <div className={`flex flex-col ${spacingConfig.gap}`}>
            <YieldBearingAssetAboutSection
              asset={asset}
              showShortDescription={false}
              showHistoricalPerformance
              onBrochureHelpClick={handleClickOnYieldBearingAssetBrochureButton}
            />
            <YieldBearingAssetCompositionSection
              yieldBearingAsset={asset}
              underlyingYieldBearingAssets={asset.underlyingYieldBearingAssets}
              showHeader
              showDescription
              showHistoricalPerformanceCharts
              showCompositionDescription
              headerRightNode={
                !isSingleAsset && (
                  <QuestionButton onClick={handleClickOnHelpButton} />
                )
              }
              onBrochureHelpClick={handleClickOnBrochureButton}
            />
          </div>
        ),
      },
    ],
    [
      asset,
      isSingleAsset,
      handleClickOnHelpButton,
      handleClickOnBrochureButton,
      handleClickOnYieldBearingAssetBrochureButton,
    ],
  );
  return (
    <Tray
      isOpen={isOpen}
      onClose={onClose}
      screens={screens}
      className="overflow-hidden"
      bottomDrawer={{
        isOpen: isBottomDrawerOpen,
        showHeaderCloseButton: true,
        onClose: () => setIsBottomDrawerOpen(false),
        headerTitle: bottomDrawerContent.headerTitle,
        headerContent: bottomDrawerContent.headerContent,
        bodyChildren: bottomDrawerContent.bodyChildren,
        includeFooterCloseButton: false,
      }}
    />
  );
};
const YieldBearingAssetAdvancedDetailsTray: FC<
  YieldBearingAssetAdvancedDetailsTrayProps
> = (props) => (
  <TrayNavigationProvider>
    <YieldBearingAssetAdvancedDetailsTrayContent {...props} />
  </TrayNavigationProvider>
);
export default YieldBearingAssetAdvancedDetailsTray;
